import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus } from "lucide-react";
import { CTAButton, MaskedLines, Reveal, SectionTag, EASE } from "../components/ui-bits";
import { FAQS, IMAGES, SPEC_TABLE } from "../content";

function FaqItem({ item, index, open, onToggle }) {
    return (
        <div className={`border-inkline ${index !== 0 ? "border-t" : ""}`} data-testid={`faq-item-${index}`}>
            <button
                data-testid={`faq-toggle-${index}`}
                onClick={onToggle}
                aria-expanded={open}
                className="group flex w-full items-center justify-between gap-6 py-6 text-left"
            >
                <span className="flex items-baseline gap-4">
                    <span className="font-display text-sm font-bold text-bluebrand">{String(index + 1).padStart(2, "0")}</span>
                    <span className="font-display text-lg font-bold tracking-tight transition-colors duration-300 group-hover:text-bluebrand md:text-xl">
                        {item.q}
                    </span>
                </span>
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center border border-inkline transition-[background-color,color] duration-300 ${open ? "bg-ink text-bone" : ""}`}>
                    {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </span>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: EASE }}
                        className="overflow-hidden"
                    >
                        <p className="max-w-3xl pb-7 pl-9 text-sm leading-relaxed text-smoke md:pl-11 md:text-base" data-testid={`faq-answer-${index}`}>
                            {item.a}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function ComplianceGuide() {
    const [open, setOpen] = useState(0);
    return (
        <div data-testid="compliance-guide-page">
            <section className="blueprint-grid border-b border-inkline">
                <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-6 pb-16 pt-16 md:px-10 lg:grid-cols-12 lg:gap-6 lg:pb-24 lg:pt-24">
                    <div className="lg:col-span-7">
                        <SectionTag>Compliance guide</SectionTag>
                        <MaskedLines
                            lines={["THE RULES,", "TRANSLATED."]}
                            className="font-display text-[13vw] font-bold leading-[0.9] tracking-tighter sm:text-7xl lg:text-[6rem]"
                            accentIndexes={[1]}
                        />
                        <Reveal delay={0.5}>
                            <p className="mt-8 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
                                Plain-English answers to the questions building owners actually ask — with the exact
                                code sections cited, so you can verify every word.
                            </p>
                        </Reveal>
                    </div>
                    <div className="flex items-end lg:col-span-5">
                        <Reveal delay={0.3} className="w-full">
                            <div className="overflow-hidden border border-inkline">
                                <img
                                    src={IMAGES.accessibility}
                                    alt="International Symbol of Accessibility signage"
                                    className="aspect-[4/3] w-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <p className="mt-3 text-xs font-semibold uppercase tracking-widest text-smoke">
                                ISA signage — ADA §703.7.2.1
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section data-testid="spec-table-section" className="border-b border-inkline bg-paper">
                <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
                    <Reveal>
                        <SectionTag>The spec sheet</SectionTag>
                        <h2 className="max-w-2xl font-display text-4xl font-bold leading-none tracking-tight md:text-6xl">
                            What code actually demands.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-14 overflow-x-auto">
                        <table className="w-full min-w-[760px] border-collapse border border-inkline text-left" data-testid="spec-table">
                            <thead>
                                <tr className="bg-ink text-bone">
                                    <th className="border border-boneline px-5 py-4 font-display text-xs font-bold uppercase tracking-widest">Requirement</th>
                                    <th className="border border-boneline px-5 py-4 font-display text-xs font-bold uppercase tracking-widest">Specification</th>
                                    <th className="border border-boneline px-5 py-4 font-display text-xs font-bold uppercase tracking-widest">Federal (2010 ADA)</th>
                                    <th className="border border-boneline px-5 py-4 font-display text-xs font-bold uppercase tracking-widest">California (CBC 11B)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {SPEC_TABLE.map((row, i) => (
                                    <tr key={row.req} className={`transition-colors duration-300 hover:bg-volt/40 ${i % 2 === 1 ? "bg-bone" : "bg-paper"}`} data-testid={`spec-row-${i}`}>
                                        <td className="border border-inkline px-5 py-4 font-display text-sm font-bold">{row.req}</td>
                                        <td className="border border-inkline px-5 py-4 text-sm text-smoke">{row.spec}</td>
                                        <td className="border border-inkline px-5 py-4 text-sm font-semibold text-bluebrand">{row.fed}</td>
                                        <td className="border border-inkline px-5 py-4 text-sm font-semibold">{row.ca}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Reveal>
                </div>
            </section>

            <section data-testid="faq-section" className="border-b border-inkline">
                <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
                    <Reveal>
                        <SectionTag>Frequently asked</SectionTag>
                        <h2 className="max-w-2xl font-display text-4xl font-bold leading-none tracking-tight md:text-6xl">
                            Answers with citations.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-14 border-y border-inkline">
                        <div>
                            {FAQS.map((f, i) => (
                                <FaqItem key={i} item={f} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-8 max-w-2xl text-xs leading-relaxed text-smoke">
                            This guide summarizes the 2010 ADA Standards for Accessible Design and the California
                            Building Code Chapter 11B for general information. It is not legal advice. For a
                            property-specific determination, request a site audit or consult a CASp inspector.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-bluebrand text-white">
                <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-8 px-6 py-20 md:flex-row md:items-center md:px-10">
                    <Reveal>
                        <h2 className="font-display text-4xl font-bold leading-none tracking-tight md:text-6xl">
                            Rather we just <span className="text-volt">handle it?</span>
                        </h2>
                        <p className="mt-4 max-w-md text-sm text-white/80 md:text-base">
                            Free site walk, full violation report, and a 48-hour quote. That's chapter one.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <CTAButton to="/get-started" variant="volt" testid="guide-cta-audit">
                            Request an audit
                        </CTAButton>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
